import { assign, fromPromise, setup } from 'xstate';
import { MARKET_ACTION_TIMEOUT_MS, MARKET_READ_TIMEOUT_MS } from '~/features/market/requestPolicy';
import { withRequestDeadline } from '~/utils/requestDeadline';
import {
  getResponseUnmaskState,
  performUnmaskAction,
  type ResponseUnmaskState,
  type UnmaskAction,
} from './responseUnmask.api';

export const UNMASK_POLL_INTERVAL_MS = 5_000;
export const UNMASK_POLL_WINDOW_MS = 60_000;

const capabilityFor = {
  request: 'canRequest',
  approve: 'canApprove',
  reject: 'canReject',
  withdraw: 'canWithdraw',
} as const;

type ConsentSnapshot = Pick<ResponseUnmaskState, 'privacyStatus' | 'requestToken'>;
const unchangedConsent = (previous: ConsentSnapshot | null, current: ResponseUnmaskState) =>
  previous?.privacyStatus === current.privacyStatus &&
  previous.requestToken === current.requestToken;
const changedBeforeApprovalMessage =
  'This request changed before your approval was sent. Review it and try again.';
type QueuedAct = {
  action: UnmaskAction;
  expectedToken?: string | null;
  termsVersion?: string | null;
};
const tokenMatches = (
  expectedToken: string | null | undefined,
  state: ResponseUnmaskState | null
) => expectedToken === undefined || expectedToken === (state?.requestToken ?? null);
// [MCV-1755] A confirmed request (the agreement was accepted) that the refreshed state no
// longer permits - say so instead of closing the dialog with nothing sent.
const changedBeforeRequestMessage =
  'This request can no longer be sent; the state changed. Review the current state and try again.';
const isConfirmedAct = (event: { type: string; action?: UnmaskAction; expectedToken?: unknown }) =>
  event.type === 'ACT' && (event.expectedToken !== undefined || event.action === 'request');
const unconfirmedActionMessage =
  'The action could not be confirmed. Review the refreshed state before trying again.';

export const responseUnmaskMachine = setup({
  types: {
    input: {} as { responseId: number; visible?: boolean; attached?: boolean },
    context: {} as {
      responseId: number;
      state: ResponseUnmaskState | null;
      action: UnmaskAction | null;
      // [MCV-1755] The agreement version the user accepted for the action in flight.
      termsVersion: string | null;
      error: string | null;
      revision: number;
      notifiedRevisions: Record<string, number>;
      visible: boolean;
      pollUntil: number;
      refreshRequested: boolean;
      failedConsent: ConsentSnapshot | null;
      attached: boolean;
      viewValidated: boolean;
      recovering: boolean;
      confirmedAction: UnmaskAction | null;
      queuedAct: QueuedAct | null;
    },
    events: {} as
      | { type: 'REFRESH'; restartPolling?: boolean }
      | { type: 'VISIBILITY'; visible: boolean }
      | { type: 'ATTACH'; visible: boolean }
      | { type: 'DETACH' }
      | { type: 'VIEW_NOTIFIED'; viewKey: string; revision: number }
      | {
          type: 'ACT';
          action: UnmaskAction;
          expectedToken?: string | null;
          // Set when the user accepted the Platform Participation Agreement for this action.
          termsVersion?: string | null;
        },
  },
  actors: {
    read: fromPromise(({ input, signal }: { input: { responseId: number }; signal: AbortSignal }) =>
      withRequestDeadline(
        (requestSignal) => getResponseUnmaskState(input.responseId, requestSignal),
        MARKET_READ_TIMEOUT_MS,
        signal
      )
    ),
    mutate: fromPromise(
      ({
        input,
        signal,
      }: {
        input: {
          responseId: number;
          action: UnmaskAction | null;
          token: string | null;
          termsVersion: string | null;
        };
        signal: AbortSignal;
      }) => {
        if (!input.action) throw new Error('No consent action selected.');
        const action = input.action;
        return withRequestDeadline(
          (requestSignal) =>
            performUnmaskAction(
              input.responseId,
              action,
              input.token,
              requestSignal,
              input.termsVersion
            ),
          MARKET_ACTION_TIMEOUT_MS,
          signal
        );
      }
    ),
  },
  guards: {
    canAct: ({ context, event }) =>
      context.attached &&
      context.viewValidated &&
      event.type === 'ACT' &&
      context.state?.[capabilityFor[event.action]] === true,
    canActOnToken: ({ context, event }) =>
      context.attached &&
      context.viewValidated &&
      event.type === 'ACT' &&
      context.state?.[capabilityFor[event.action]] === true &&
      tokenMatches(event.expectedToken, context.state),
    // [MCV-1755] A confirmation dialog can outlive the poll that put the machine in
    // `loading`; keep the confirmed action and apply it to the refreshed request.
    // Only confirmed actions are queued: approvals (they carry the token the dialog
    // showed) and requests (the agreement was accepted; they have no token, but are
    // re-checked against canRequest on the refreshed state). A direct reject/withdraw
    // has nothing to re-check and would surface the approval-specific "changed" message.
    canQueueAct: ({ context, event }) =>
      context.attached && context.viewValidated && !context.recovering && isConfirmedAct(event),
    // A confirmed action that cannot even be queued (e.g. mid-recovery) must not vanish.
    isConfirmedAct: ({ event }) => isConfirmedAct(event),
    queuedActAllowed: ({ context }) => {
      const queued = context.queuedAct;
      return (
        queued !== null &&
        context.attached &&
        context.viewValidated &&
        context.state?.[capabilityFor[queued.action]] === true &&
        tokenMatches(queued.expectedToken, context.state)
      );
    },
    queuedActStale: ({ context }) => context.queuedAct !== null,
    shouldPoll: ({ context }) =>
      context.attached &&
      context.visible &&
      context.state?.privacyStatus === 'PENDING' &&
      Date.now() < context.pollUntil,
  },
}).createMachine({
  id: 'responseUnmask',
  initial: 'starting',
  context: ({ input }) => ({
    responseId: input.responseId,
    state: null,
    action: null,
    termsVersion: null,
    error: null,
    revision: 0,
    notifiedRevisions: {},
    visible: input.visible ?? true,
    pollUntil: 0,
    refreshRequested: false,
    failedConsent: null,
    attached: input.attached ?? true,
    viewValidated: false,
    recovering: false,
    confirmedAction: null,
    queuedAct: null,
  }),
  on: {
    VIEW_NOTIFIED: {
      guard: ({ context, event }) =>
        event.revision === context.revision &&
        event.revision > (context.notifiedRevisions[event.viewKey] ?? 0),
      actions: assign({
        notifiedRevisions: ({ context, event }) => ({
          ...context.notifiedRevisions,
          [event.viewKey]: event.revision,
        }),
      }),
    },
    VISIBILITY: { actions: assign({ visible: ({ event }) => event.visible }) },
    ATTACH: {
      actions: assign({
        attached: true,
        visible: ({ event }) => event.visible,
        viewValidated: false,
      }),
    },
    DETACH: {
      actions: assign({
        attached: false,
        visible: false,
        viewValidated: false,
        refreshRequested: false,
        queuedAct: null,
      }),
    },
  },
  states: {
    starting: {
      always: [
        { guard: ({ context }) => context.attached, target: 'loading' },
        { target: 'dormant' },
      ],
    },
    dormant: {
      on: {
        ATTACH: {
          target: 'loading',
          actions: assign({
            attached: true,
            visible: ({ event }) => event.visible,
            viewValidated: false,
          }),
        },
      },
    },
    loading: {
      on: {
        REFRESH: {
          guard: ({ context }) => !context.recovering,
          actions: assign({ refreshRequested: true }),
        },
        ACT: [
          {
            guard: 'canQueueAct',
            actions: assign({
              queuedAct: ({ event }) => ({
                action: event.action,
                expectedToken: event.expectedToken,
                termsVersion: event.termsVersion,
              }),
            }),
          },
          {
            guard: 'isConfirmedAct',
            actions: assign({
              error: ({ event }) =>
                event.action === 'request'
                  ? changedBeforeRequestMessage
                  : changedBeforeApprovalMessage,
            }),
          },
        ],
        ATTACH: {
          guard: ({ context }) => !context.attached,
          actions: assign({
            attached: true,
            visible: ({ event }) => event.visible,
            viewValidated: false,
            refreshRequested: true,
          }),
        },
        DETACH: [
          {
            guard: ({ context }) => !context.recovering,
            target: 'dormant',
            actions: assign({
              attached: false,
              visible: false,
              viewValidated: false,
              refreshRequested: false,
              queuedAct: null,
            }),
          },
          {
            actions: assign({
              attached: false,
              visible: false,
              viewValidated: false,
              refreshRequested: false,
              queuedAct: null,
            }),
          },
        ],
      },
      invoke: {
        src: 'read',
        input: ({ context }) => ({ responseId: context.responseId }),
        onDone: [
          {
            guard: ({ context }) => context.refreshRequested && context.attached,
            target: 'loading',
            reenter: true,
            actions: assign({ refreshRequested: false, recovering: false }),
          },
          {
            target: 'settled',
            actions: assign({
              state: ({ event }) => event.output,
              viewValidated: ({ context }) => context.attached,
              recovering: false,
              confirmedAction: null,
              error: ({ context, event }) =>
                unchangedConsent(context.failedConsent, event.output)
                  ? unconfirmedActionMessage
                  : null,
              failedConsent: ({ context, event }) =>
                unchangedConsent(context.failedConsent, event.output)
                  ? context.failedConsent
                  : null,
              revision: ({ context, event }) =>
                context.state && JSON.stringify(context.state) !== JSON.stringify(event.output)
                  ? context.revision + 1
                  : context.revision,
              pollUntil: ({ context, event }) =>
                event.output.requestToken &&
                event.output.requestToken !== context.state?.requestToken
                  ? Date.now() + UNMASK_POLL_WINDOW_MS
                  : context.pollUntil,
            }),
          },
        ],
        onError: {
          target: 'error',
          actions: assign({
            viewValidated: false,
            recovering: false,
            queuedAct: null,
            error: ({ context }) =>
              context.confirmedAction
                ? 'Your identity action succeeded, but current permissions could not be refreshed. Refresh before taking another action.'
                : 'Unable to refresh identity permissions. Refresh before taking another action.',
          }),
        },
      },
    },
    settled: {
      always: [
        {
          target: 'mutating',
          guard: 'queuedActAllowed',
          actions: assign({
            action: ({ context }) => context.queuedAct?.action ?? null,
            termsVersion: ({ context }) => context.queuedAct?.termsVersion ?? null,
            queuedAct: null,
            error: null,
            failedConsent: null,
            confirmedAction: null,
          }),
        },
        {
          target: 'ready',
          guard: 'queuedActStale',
          actions: assign({
            error: ({ context }) =>
              context.queuedAct?.action === 'request'
                ? changedBeforeRequestMessage
                : changedBeforeApprovalMessage,
            queuedAct: null,
          }),
        },
        { target: 'dormant', guard: ({ context }) => !context.attached },
        { target: 'ready' },
      ],
    },
    ready: {
      after: {
        [UNMASK_POLL_INTERVAL_MS]: { target: 'loading', guard: 'shouldPoll' },
      },
      on: {
        ATTACH: {
          target: 'loading',
          actions: assign({
            attached: true,
            visible: ({ event }) => event.visible,
            viewValidated: false,
          }),
        },
        DETACH: {
          target: 'dormant',
          actions: assign({ attached: false, visible: false, viewValidated: false }),
        },
        VISIBILITY: {
          target: 'ready',
          reenter: true,
          actions: assign({ visible: ({ event }) => event.visible }),
        },
        REFRESH: {
          target: 'loading',
          actions: assign({
            pollUntil: ({ context, event }) =>
              event.restartPolling === false
                ? context.pollUntil
                : Date.now() + UNMASK_POLL_WINDOW_MS,
          }),
        },
        ACT: [
          {
            target: 'mutating',
            guard: 'canActOnToken',
            actions: assign({
              action: ({ event }) => event.action,
              termsVersion: ({ event }) => event.termsVersion ?? null,
              error: null,
              failedConsent: null,
              confirmedAction: null,
            }),
          },
          {
            // A confirmed action (it carries the token the dialog showed) that can
            // no longer run - the request was replaced or withdrawn - says so
            // rather than closing the dialog with nothing done.
            guard: 'isConfirmedAct',
            actions: assign({
              error: ({ event }) =>
                event.action === 'request'
                  ? changedBeforeRequestMessage
                  : changedBeforeApprovalMessage,
            }),
          },
        ],
      },
    },
    mutating: {
      invoke: {
        src: 'mutate',
        input: ({ context }) => ({
          responseId: context.responseId,
          action: context.action,
          token: context.state?.requestToken ?? null,
          termsVersion: context.termsVersion,
        }),
        onDone: {
          target: 'loading',
          actions: assign({
            confirmedAction: ({ context }) => context.action,
            action: null,
            termsVersion: null,
            recovering: true,
          }),
        },
        onError: {
          target: 'loading',
          actions: assign({
            action: null,
            termsVersion: null,
            recovering: true,
            failedConsent: ({ context }) => context.state,
            error: unconfirmedActionMessage,
          }),
        },
      },
    },
    error: {
      on: {
        REFRESH: { target: 'loading', actions: assign({ error: null }) },
        ATTACH: {
          target: 'loading',
          actions: assign({
            attached: true,
            visible: ({ event }) => event.visible,
            viewValidated: false,
          }),
        },
        DETACH: {
          target: 'dormant',
          actions: assign({ attached: false, visible: false, viewValidated: false }),
        },
      },
    },
  },
});
